import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
	CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateParts,
	unLinkTemplateParts,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { TemplatePart } from "../../../../models/TemplatePart";
import { PartsCatalogFreshness } from "@/shared/catalogs/components/CatalogFreshnessControls";
import { PartsTable } from "./parts-tables";

interface Props {
	searchParts: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	isLoading: boolean;
	setShowLinkResourcesToAssetDialog: Function;
}

export const PartPicker: React.FC<Props> = ({
	searchParts,
	show,
	onHide,
	TemplateId,
	isLoading,
	setShowLinkResourcesToAssetDialog,
}) => {
	const {
		state: { listAllParts, templatePartsList },
		actions: { setTemplatePartsToAdd, setListAllParts },
	} = useTemplatesContext();

	const [selectedParts, setSelectedParts] = useState<TemplatePart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			searchParts();
		}
	}, [show]);

	useEffect(() => {
		if (show) {
			setSelectedParts([...templatePartsList]);
		}
	}, [show]);

	const handleSelectionChanged = (newSelectedParts: TemplatePart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const partsToAdd = selectedParts.filter((part) => {
			const partName = (part as any).Name || part.PartName;
			return !templatePartsList.some((woPart) => woPart.PartName === partName);
		});

		const partsToRemove = templatePartsList.filter(
			(part) =>
				!selectedParts.some((selectedPart) => {
					const selectedPartName =
						(selectedPart as any).Name || selectedPart.PartName;
					return selectedPartName === part.PartName;
				}),
		);

		if (partsToAdd.length > 0) {
			const newParts = partsToAdd.map((part) => ({
				PartName: (part as any).Name || part.PartName || "",
				IsAssetResource: false,
			}));
			setTemplatePartsToAdd(partsToAdd);
			try {
				// setShowLinkResourcesToAssetDialog(true);
				const res = await linkTemplateParts(TemplateId as number, newParts);
				if (res.ok) {
					setSuccessMessage("Parts added successfully");
				} else setError(res.message);
				setShowLinkResourcesToAssetDialog(true);
			} catch (err) {
				setError("Failed to add parts");
			}
		}

		if (partsToRemove.length > 0) {
			const partsToRemoveFormatted = partsToRemove.map((part) => ({
				PartName: part.PartName ?? "",
				IsAssetResource: part.IsAssetResource,
			}));
			try {
				await unLinkTemplateParts(TemplateId as number, partsToRemoveFormatted);
				setSuccessMessage("Parts removed successfully");
			} catch (err) {
				setError("Failed to remove parts");
			}
		}

		setLoadingSave(false);
		if (partsToAdd.length > 0 || partsToRemove.length > 0) {
			onHide(true);
		} else {
			onHide(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						PARTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<PartsTable
						allParts={listAllParts}
						templateParts={templatePartsList}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
					<PartsCatalogFreshness
						onReloaded={setListAllParts}
						onError={setError}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						startIcon={loadingSave && <CircularProgress size="1rem" />}
						disabled={loadingSave}
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
